import { useCallback, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { TbBrowser, TbDeviceDesktop } from "react-icons/tb";
import "./syncMethodSwitch.scss";

export type SyncMethod = "browser" | "agent";
export type SyncMethodStatus = { tone: "ok" | "idle" | "warn"; label: string } | null;

const STORAGE_KEY = "pvsSyncMethod";
const METHODS: { key: SyncMethod; icon: React.ReactNode }[] = [
    { key: "browser", icon: <TbBrowser /> },
    { key: "agent", icon: <TbDeviceDesktop /> },
];

function readStored(): SyncMethod | null {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        return v === "browser" || v === "agent" ? v : null;
    } catch {
        return null;
    }
}

/** Which sync method the Sync page shows. The last choice wins (per browser, a convenience only);
 *  first visit falls back to the desktop app when this browser can't read folders at all, else the
 *  browser method. */
export function useSyncMethod(browserSupported: boolean): [SyncMethod, (m: SyncMethod) => void] {
    const [method, setMethodState] = useState<SyncMethod>(() => readStored() ?? (browserSupported ? "browser" : "agent"));
    const setMethod = useCallback((m: SyncMethod) => {
        setMethodState(m);
        try {
            localStorage.setItem(STORAGE_KEY, m);
        } catch {
            // private mode / blocked storage — the choice just isn't remembered
        }
    }, []);
    return [method, setMethod];
}

/** Patient sync is one method or the other, never both: two option tiles (a radio group), each with
 *  what it is and whether it is set up on this practice, and the chosen one is what the page shows. */
export function SyncMethodSwitch({ method, onChange, status }: {
    method: SyncMethod;
    onChange: (m: SyncMethod) => void;
    status: Record<SyncMethod, SyncMethodStatus>;
}) {
    const { t } = useTranslation();

    const onKey = (e: KeyboardEvent<HTMLButtonElement>) => {
        if (e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "ArrowUp" && e.key !== "ArrowDown" && e.key !== "Home" && e.key !== "End") return;
        e.preventDefault();
        const next: SyncMethod = e.key === "Home" ? "browser" : e.key === "End" ? "agent" : method === "browser" ? "agent" : "browser";
        onChange(next);
        document.getElementById(`sync-method-${next}`)?.focus();
    };

    return (
        <div className="sync-method" role="radiogroup" aria-label={t("syncMethod.label")}>
            <p className="sync-method__prompt">{t("syncMethod.prompt")}</p>
            <div className="sync-method__options">
                {METHODS.map(({ key, icon }) => {
                    const selected = method === key;
                    const s = status[key];
                    return (
                        <button
                            key={key}
                            type="button"
                            role="radio"
                            id={`sync-method-${key}`}
                            aria-checked={selected}
                            tabIndex={selected ? 0 : -1}
                            className={`sync-method__option${selected ? " sync-method__option--selected" : ""}`}
                            onClick={() => onChange(key)}
                            onKeyDown={onKey}
                        >
                            <span className="sync-method__icon" aria-hidden="true">{icon}</span>
                            <span className="sync-method__text">
                                <span className="sync-method__title">{t(`syncMethod.${key}.title`)}</span>
                                <span className="sync-method__desc">{t(`syncMethod.${key}.caption`)}</span>
                                {s && <span className={`sync-method__status sync-method__status--${s.tone}`}>{s.label}</span>}
                            </span>
                            <span className="sync-method__radio" aria-hidden="true" />
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
